import type {
    AvatarCustomization,
    AvatarItemKey,
    BannerData,
    ItemData,
    UserData,
} from "./Customization";

type dbcon = {
    [key: string]: ItemData[];
};

export type DatabaseContent = dbcon & {
    banners: BannerData[];
};

export type ItemFilter = {
    isPremium?: boolean;
    colorStyle?: string;
    name?: string;
};

export type ItemDatabaseMethod = keyof ItemDatabase;
export class ItemDatabase {
    private _data: DatabaseContent | null = null;
    private _loaded: boolean = false;
    public fileRoot: string = "";

    public onReady: () => void = () => {};
    constructor(databaseUrl: string) {
        this.load(databaseUrl);
    }

    async load(databaseUrl: string) {
        // Data already loaded
        if (this._data) return this._data;
        try {
            const response = await fetch(databaseUrl);
            const json = await response.json();

            for (const category in json.data) {
                json.data[category] = json.data[category].map(
                    (item: ItemData) => ({
                        ...item,
                        isPremium: item.price !== 0,
                        isForSale: (item.price ?? -1) > 0,
                    }),
                );
            }

            this._data = json.data;

            this._loaded = true;

            if (this.onReady) this.onReady();
            return this._data;
        } catch (err) {
            console.error("Unable to load data from url:", databaseUrl);
        }
    }

    get isReady() {
        return this._loaded;
    }

    _filter(array: ItemData[] | null | undefined, filter: ItemFilter) {
        if (!array) return null;

        // Filter by price
        if ("isPremium" in filter)
            array = array.filter((e) => e.isPremium === filter.isPremium);

        // Filter by color style
        if ("colorStyle" in filter)
            array = array.filter((e) => e.colorStyle === filter.colorStyle);

        // Filter by name
        if (filter.name && filter.name.length > 0)
            array = array.filter((e) => e.name.includes(filter.name ?? ""));

        return array;
    }

    getBackAccs = (filter = {}) => this._filter(this._data?.back_acc, filter);
    getBanners = (filter = {}) => this._filter(this._data?.banner, filter);
    getBodies = (filter = {}) => this._filter(this._data?.body, filter);
    getBodyAccs = (filter = {}) => this._filter(this._data?.body_acc, filter);
    getClothes = (filter = {}) => this._filter(this._data?.clothes, filter);
    getEffects = (filter = {}) => this._filter(this._data?.effects, filter);
    getEyes = (filter = {}) => this._filter(this._data?.eyes, filter);
    getFaceAccs = (filter = {}) => this._filter(this._data?.face_acc, filter);
    getHeads = (filter = {}) => this._filter(this._data?.head, filter);
    getHeadAccs = (filter = {}) => this._filter(this._data?.head_acc, filter);
    getMouths = (filter = {}) => this._filter(this._data?.mouth, filter);

    // Returns a specific item from the category collection
    // Returns the item with the given ID, or null if not found
    getItem = (collection: ItemData[] | string, id: string) => {
        if (!this._data || !collection || !id) return null;

        if (typeof collection === "string") {
            // If collection is a string, assume it's a type and fetch the data
            collection = this._data[collection];
        }

        let str = collection.find((item) => item.id === id);
        if (!str) return null;
        let result = JSON.parse(JSON.stringify(str)); // Clone the item to avoid reference issues
        return result;
    };

    // Finds an item by its ID across all types
    // Returns the first item found with the matching ID
    // Returns null if no item is found
    findItem = (id: string) => {
        if (!id) return null;

        for (const type in this._data) {
            const item = this._data[type].find((item) => item.id === id);
            if (item) return item;
        }
        return null;
    };

    translateAvatarData = (data: AvatarCustomization): AvatarCustomization => {
        if (!data) return data;

        // Translate the avatar customization data to match the database structure
        const translated = {
            ...data,
            effects: {
                ...this.getItem("effects", data?.effects?.id),
                colorMap: data.effects?.colorMap ?? [],
            },
            head: {
                ...this.getItem("head", data?.head?.id),
                colorMap: data.head?.colorMap ?? [],
            },
            eyes: {
                ...this.getItem("eyes", data?.eyes?.id),
                colorMap: data.eyes?.colorMap ?? [],
            },
            mouth: {
                ...this.getItem("mouth", data?.mouth?.id),
                colorMap: data.mouth?.colorMap ?? [],
            },
            clothes: {
                ...this.getItem("clothes", data?.clothes?.id),
                colorMap: data.clothes?.colorMap ?? [],
            },
            body: {
                ...this.getItem("body", data?.body?.id),
                colorMap: data.body?.colorMap ?? [],
            },
            face_acc: {
                ...this.getItem("face_acc", data?.face_acc?.id),
                colorMap: data.face_acc?.colorMap ?? [],
            },
            head_acc: {
                ...this.getItem("head_acc", data?.head_acc?.id),
                colorMap: data.head_acc?.colorMap ?? [],
            },
            body_acc: {
                ...this.getItem("body_acc", data?.body_acc?.id),
                colorMap: data.body_acc?.colorMap ?? [],
            },
            back_acc: {
                ...this.getItem("back_acc", data?.back_acc?.id),
                colorMap: data.back_acc?.colorMap ?? [],
            },
            banner: {
                ...this.getItem("banner", data?.banner?.id),
                colorMap: data.banner?.colorMap ?? [],
            },
            fileRoot: this.fileRoot,
        } as AvatarCustomization;
        if (this.fileRoot) translated.fileRoot = this.fileRoot;

        return translated;
    };

    filterUserData = (userData: UserData) => {
        // Remove any items the avatar can't actually use at the moment.
        const head = userData.avatar.head;
        const body = userData.avatar.body;

        const filter = {
            requiredHeadStyle: head.style,
            requiredBodyStyle: body.style,
        };

        // Iterate through every key
        for (const key in userData.avatar) {
            const idata = userData.avatar[key];

            // If the item at the given key requires a specific head/body style
            // then that item cannot be drawn. Fill the data with an empty object.
            if (
                ((idata.requiredHeadStyle?.length ?? 0) > 0 &&
                    !idata.requiredHeadStyle.includes(
                        filter.requiredHeadStyle,
                    )) ||
                ((idata.requiredBodyStyle?.length ?? 0) > 0 &&
                    !idata.requiredBodyStyle.includes(filter.requiredBodyStyle))
            )
                userData.avatar[key] = {
                    id: "",
                    name: "",
                    colorMap: [],
                } as ItemData;
        }
        return userData;
    };

    getRandomDesign = (): AvatarCustomization => {
        const loadOrder = [
            "effects",
            "back_acc",
            "body",
            "clothes",
            "head",
            "eyes",
            "mouth",
            "face_acc",
            "head_acc",
            "body_acc",
        ] as AvatarItemKey[];

        const skinColors = [
            // Natural skin tones
            "#C19C9C", // Pale
            "#FBE8EB", // Fair / Light
            "#F3D1B0", // Light Medium
            "#D1A173", // Medium / Olive
            "#A16641", // Tan / Warm Brown
            "#4B2E2B", // Deep / Dark Brown

            // Fantasy / Crazy skin tones
            "#6A0DAD", // Royal Purple
            "#B22222", // Blood Red
            "#99FFFF", // Ice Blue
            "#228B22", // Forest Green
            "#D6A4FF", // Pale Lavender
            "#FF69B4", // Hot Pink Demon Glow
            "#00FF00",
        ];

        const colors = [
            // Natural clothing tones
            "#1C1C1C", // Black
            "#FFFFFF", // White
            "#808080", // Gray
            "#3B2F2F", // Brown
            "#1E3A5F", // Navy Blue

            // Fantasy / Crazy clothing tones
            "#FF4500", // Neon Orange
            "#FFD700", // Metallic Gold
            "#7CFC00", // Electric Lime
            "#FF00FF", // Magenta / Arcane Pink
            "#00CED1", // Bright Teal
            "#9400D3", // Deep Violet
        ];

        const intInRange = (max = 1, min = 0): number => {
            const range = max - min;
            const sel = Math.floor(Math.random() * range);
            return sel + min;
        };
        const free = { isPremium: false };

        const headOpts = this.getHeads(free) ?? [];
        const eyeOpts = this.getEyes(free) ?? [];
        const mouthOpts = this.getMouths(free) ?? [];
        const clothesOpts = this.getClothes(free) ?? [];
        const bodyOpts = this.getBodies(free) ?? [];
        const effectsOpts = this.getEffects(free) ?? [];

        const faceAccOpts = this.getFaceAccs(free) ?? [];
        faceAccOpts.push({} as ItemData);

        const headAccOpts = this.getHeadAccs(free) ?? [];
        headAccOpts.push({} as ItemData);

        const bodyAccOpts = this.getBodyAccs(free) ?? [];
        bodyAccOpts.push({} as ItemData);

        const backAccOpts = this.getBackAccs(free) ?? [];
        backAccOpts.push({} as ItemData);

        const clothes = clothesOpts[intInRange(clothesOpts.length)];
        clothes.colorMap = [];
        for (let i = 0; i < (clothes.colorZones?.length ?? 0); i++)
            clothes.colorMap.push(colors[intInRange(colors.length)]);

        const randomItem = (collection: ItemData[]): ItemData => {
            const itm = collection[intInRange(collection.length)];
            if (itm) {
                itm.colorMap = [];
                if ("colorZones" in itm)
                    for (let i = 0; i < (itm.colorZones?.length ?? 0); i++)
                        itm.colorMap.push(colors[intInRange(colors.length)]);
                else itm.colorMap.push(colors[intInRange(colors.length)]);
            }
            return itm;
        };
        const eyes = randomItem(eyeOpts);
        const mouth = randomItem(mouthOpts);
        const face_acc = randomItem(faceAccOpts);
        const head_acc = randomItem(headAccOpts);
        const effects = randomItem(effectsOpts);

        // Generate fully fleshed out Avatar Customization data.
        return {
            head: headOpts[intInRange(headOpts.length)], //heads[headOffset + skinColor],
            eyes: eyes,
            mouth: mouth,
            clothes: clothes,
            body: bodyOpts[intInRange(bodyOpts.length)],
            face_acc: face_acc,
            head_acc: head_acc,
            body_acc: { id: "", name: "" },
            back_acc: { id: "", name: "" },
            banner: { id: "", name: "" },
            effects: effects,
            loadOrder: loadOrder,
            skin_color: skinColors[intInRange(skinColors.length)],
            zindex: 0,
            font: { name: "Arial", size: 20, color: "#FFFFFF" },
        };
    };
}
